import React, { useEffect, useRef, useState, useMemo } from 'react'
import moment from 'moment'
import 'moment-precise-range-plugin'
import { Menu, Icon } from '@material-ui/core'
import { injectIntl } from 'react-intl'
import { useVisibilityHook } from 'react-observer-api'
import { connect } from 'react-redux'
import cn from 'classnames'
import isEmpty from 'lodash/isEmpty'
import { TABLET_BREAKPOINT, LAPTOP_BREAKPOINT } from 'helpers/responsive'
import { ReactComponent as LockIcon } from 'assets/img/ic_lock.svg'
import ProgressBar, { createProgressList } from 'components/ProgressBar'
import { roundTo } from 'helpers/math'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import SidePopover from 'components/common/Popover/SidePopover'
import {
  TYPE_INPUT_BOOLEAN,
  TYPE_INPUT_COUNTER,
  TYPE_INPUT_CALCULATED,
  TYPE_INPUT_NUMBER,
} from 'helpers/jobs'
import { getPreviewInputDateFormat } from 'helpers/dates'
import PreviewInputValues from 'routes/App/Apps/MyJobs/Common/PreviewInputValues'
import ExtraMenu from 'routes/App/Apps/MyJobs/ExtraMenu'
import messages from 'routes/App/Apps/MyJobs/translate'
import ProfileHelper from 'helpers/profile'
import JobUnits from './JobUnits'
import JobItemTitle from './JobItemTitle'
import { JobItemContainer, CreatedBy, StyledPaper } from './JobItem.styles'

function JobItem({
  intl: { formatMessage },
  jobItem,
  activeJobId,
  reloadJobList,
  resetJobData,
  profileInfo: { grantedDeleteJobs, grantedDeleteJobSeries, name: profileName },
  isScrolled,
  handleScrollChange,
  isNewJob,
  onJobMoreInfoClick,
  previewInputs,
  createdBy: { name },
  disabledBtn,
  licensee: { usaDateFormat, usaTimeFormat, timezone: licenseeTimezone },
  site,
}) {
  const {
    id,
    title,
    number,
    end_date_time,
    start_date_time,
    allowed_locus_groups,
    allowed_locuses,
    overdue,
    tasks,
    future,
    tasks_total: total,
    tasks_in_progress: progressed,
    tasks_completed: completed,
  } = jobItem
  const formatPreview = getPreviewInputDateFormat(usaTimeFormat)
  const { code: currentCode } = ProfileHelper.getCurrentLocale()
  const isJobLocked = !!(
    start_date_time && moment(start_date_time).isAfter(moment())
  )

  const progressList = createProgressList([
    {
      className: 'pg-complete',
      tooltip: formatMessage(messages.jobsComplete, {
        complete: completed,
        total,
      }),
      value: total < 1 || completed < 1 ? 0 : roundTo(completed / total, 3, 1),
    },
    {
      className: 'pg-in-progress',
      tooltip: formatMessage(messages.jobsInProgress, {
        progress: progressed,
        total: total - completed,
      }),
      value:
        total < 1 || progressed < 1 ? 0 : roundTo(progressed / total, 3, 1),
    },
  ])

  let timeTillOverdue = ''

  if (end_date_time && !overdue && !future) {
    const { days, hours, minutes } = moment.preciseDiff(
      new Date().toLocaleString(currentCode, {
        timeZone: site?.timezone || licenseeTimezone,
      }),
      end_date_time,
      true,
    )

    if (days) {
      timeTillOverdue += `${days}${formatMessage(messages.daySymbol)}`

      if (hours) {
        timeTillOverdue += ` ${hours}${formatMessage(messages.hourSymbol)}`
      }
    } else if (!hours) {
      timeTillOverdue = `${minutes}${formatMessage(messages.minuteSymbol)}`
    } else {
      timeTillOverdue = `${hours}${formatMessage(messages.hourSymbol)}`
    }
  }

  const { setElement, isVisible } = useVisibilityHook()
  const [anchor, setAnchor] = useState(null)
  const [isExtraMenuOpen, setIsExtraMenuOpen] = useState(false)
  const jobRef = useRef()
  const unitsBlockRef = useRef()

  useEffect(() => {
    if (!isScrolled && isNewJob && activeJobId === id) {
      setTimeout(() => {
        if (jobRef.current) {
          jobRef.current.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          })
          handleScrollChange(!isScrolled)
        }
      })
    }
  })

  const onOpenExtraMenu = event => {
    event.stopPropagation()

    if (ifWindowWidthLessThan(LAPTOP_BREAKPOINT)) {
      setIsExtraMenuOpen(!isExtraMenuOpen)
    } else {
      setAnchor(event.currentTarget)
    }
  }

  const onCloseMenuExtra = () => {
    setAnchor(null)
  }

  const onCloseDrawerExtra = () => {
    setIsExtraMenuOpen(false)
  }

  const onJobInfoClick = () => {
    if (!moment(start_date_time).isAfter(moment())) {
      onJobMoreInfoClick(jobItem)
    }
  }

  const renderCreatorName = () => {
    const creatorName =
      name && name.toLowerCase() === profileName.toLowerCase()
        ? formatMessage(messages.me)
        : name

    return (
      <CreatedBy justify="space-between">
        # {number}
        <span>{creatorName}</span>
      </CreatedBy>
    )
  }

  const previewList = previewInputs.filter(
    ({ jobId, value, type }) =>
      jobId === id &&
      ((Array.isArray(value) ? !!value.length : !!value) ||
        type === TYPE_INPUT_BOOLEAN ||
        ((type === TYPE_INPUT_NUMBER ||
          type === TYPE_INPUT_COUNTER ||
          type === TYPE_INPUT_CALCULATED) &&
          value === 0)),
  )

  const isActive = useMemo(
    () => !ifWindowWidthLessThan(TABLET_BREAKPOINT) && id === activeJobId,
    [window.innerWidth, id, activeJobId],
  )

  return (
    <>
      <div ref={setElement} className="job-lazy-trigger-ref" />
      {isVisible ? (
        <>
          <StyledPaper
            isActive={isActive}
            className={cn('job-item', {
              'active-job':
                !ifWindowWidthLessThan(TABLET_BREAKPOINT) && isActive,
            })}
            onClick={onJobInfoClick}
            ref={jobRef}>
            <JobItemContainer isActive={isActive}>
              <JobItemTitle
                timeTillOverdue={timeTillOverdue}
                formatMessage={formatMessage}
                jobItem={jobItem}
                grantedDeleteJobs={grantedDeleteJobs}
                grantedDeleteJobSeries={grantedDeleteJobSeries}
                onOpenExtraMenu={onOpenExtraMenu}
                usaDateFormat={usaDateFormat}
                formatPreview={formatPreview}
                usaTimeFormat={usaTimeFormat}
                isActive={isActive}
                licenseeTimezone={licenseeTimezone}
              />
              <div className="sf-job-title-box">
                <div className="sf-job-title">
                  {isJobLocked && <LockIcon className="sf-job-locked" />}
                  {title}
                </div>
              </div>
              <div className="sf-progressbar-row">
                <ProgressBar progressList={progressList} />
                <div className="sf-progress-counter">
                  {formatMessage(messages.completeOfTotal, {
                    completed,
                    total,
                  })}
                </div>
              </div>
              <PreviewInputValues previewList={previewList} />
              <div className="sf-job-units-list-box">
                {(!isEmpty(allowed_locus_groups) ||
                  !isEmpty(allowed_locuses)) && (
                  <div className="sf-job-units-list" ref={unitsBlockRef}>
                    <Icon>place</Icon>
                    <JobUnits jobItem={jobItem} />
                  </div>
                )}
              </div>
            </JobItemContainer>
            {renderCreatorName()}
          </StyledPaper>
          {ifWindowWidthLessThan(LAPTOP_BREAKPOINT) ? (
            <SidePopover
              open={isExtraMenuOpen}
              close={onCloseDrawerExtra}
              className="extra-menu"
              position="bottom">
              <ExtraMenu
                tasks={tasks}
                job={jobItem}
                activeJobId={activeJobId}
                onCloseMenu={onCloseDrawerExtra}
                reloadJobList={reloadJobList}
                resetJobData={resetJobData}
                disabledBtn={disabledBtn}
              />
            </SidePopover>
          ) : (
            <Menu
              keepMounted
              anchorEl={anchor}
              open={!!anchor}
              onClose={onCloseMenuExtra}>
              <ExtraMenu
                tasks={tasks}
                job={jobItem}
                activeJobId={activeJobId}
                onCloseMenu={onCloseMenuExtra}
                reloadJobList={reloadJobList}
                resetJobData={resetJobData}
                disabledBtn={disabledBtn}
              />
            </Menu>
          )}
        </>
      ) : (
        <div className="job-item-placeholder" />
      )}
    </>
  )
}

export default connect(state => ({
  licensee: state.getIn(['profile', 'profile', 'licensee']),
  site: state.getIn(['profile', 'profile', 'site']),
}))(injectIntl(JobItem))
